import { Box, Paper, Typography, useTheme } from "@mui/material";
import useFitText from "../hooks/useFitText";
import { useTenant } from "../contexts/TenantContext";
import { sectionTitleSx, tint } from "../utils/textStyles";
import { isDarkTheme, isNeonTheme } from "../utils/themeTraits";

// Every card is this wide, whatever it holds: the funnel's steps, the auth pages, the legal ones.
// It is the default, so the cards line up with each other and a long paragraph cannot stretch one
// of them across the screen. On a phone a card takes the whole row, since 70vw is too narrow for
// a form there. `width`, `minWidth` and `maxWidth` are the way out for a card that fills something
// else instead, like the profile's column.
const COMMON_WIDTH = { xs: "100%", sm: "60vw", md: "50vw", lg: "40vw", xl: "30vw" };

const Article = ({
  width = "common",
  minWidth,
  maxWidth,
  xsHorizontalPadding = 5,
  title,
  titleColor,
  titleVariant = "h4",
  alignItems = "center",
  children,
  backgroundColor,
  sx = {},
}) => {
  const { displayConfig } = useTenant();
  const theme = useTheme();
  const accent = displayConfig?.primaryColor;
  // A title keeps its variant's size where it fits on one line and shrinks where it does not: a
  // long one on a phone used to run out of the card.
  const { boxRef: titleRef, textRef: titleTextRef, fitSx: titleFitSx } =
    useFitText(title, theme.typography[titleVariant]?.fontSize);

  const resolvedBackgroundColor = backgroundColor ?? (displayConfig?.sectionBackground || "#fff");

  // Dark themes edge the card in the tenant's colour: a glow on the neon one, a hairline and a
  // plain black shadow on the other. Paper keeps MUI's elevation.
  const darkSx = isNeonTheme(displayConfig) ? {
    border: `1px solid ${tint(accent, 0.4)}`,
    boxShadow: `0 0 20px ${tint(accent, 0.15)}, inset 0 0 10px ${tint(accent, 0.03)}`,
  } : isDarkTheme(displayConfig) ? {
    border: `1px solid ${tint(accent, 0.3)}`,
    boxShadow: `0 18px 40px -24px ${tint("#000", 0.9)}`,
  } : {};

  return (
    <Paper
      elevation={3}
      component="article"
      sx={{
        width: width === "common" ? COMMON_WIDTH : width,
        minWidth: minWidth === "common" ? COMMON_WIDTH : minWidth,
        maxWidth,
        borderRadius: 4,
        // Without it the padding is added *outside* the width: a card told to fill its
        // parent then hangs 80px over the right edge. Nothing in the app loads MUI's
        // CssBaseline, so no border-box is inherited from anywhere.
        boxSizing: "border-box",
        py: 5,
        px: { xs: xsHorizontalPadding, sm: 5 },
        display: "flex",
        flexDirection: "column",
        alignItems,
        backgroundColor: resolvedBackgroundColor,
        ...darkSx,
        ...sx,
      }}
    >
      {title &&
        <Typography
          ref={titleRef}
          align="center"
          variant={titleVariant}
          fontWeight="bold"
          noWrap
          mb={4}
          // A card's heading is a section heading and is lettered like every other one.
          // `titleColor` is for the heading that means something else — the error page's red.
          // The whole row, not the text's own width: the room it measures is the card's.
          sx={{ ...sectionTitleSx(displayConfig, titleColor), alignSelf: "stretch", ...titleFitSx }}
        >
          {/* Inline-block, so its width can be observed: it changes when the web font arrives. */}
          <Box component="span" ref={titleTextRef} sx={{ display: "inline-block" }}>{title}</Box>
        </Typography>
      }
      {children}
    </Paper>
  );
};

export default Article;